import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { resolveUrl } from "../lib/api";
import { fetchOffersCount } from "../lib/offersCache";
import { LogOut, User } from "lucide-react";

const BASE_NAV = [
  { href: "/#fleet", ro: "Masini",   en: "Fleet",   always: true },
  { href: "/oferte",  ro: "Oferte",  en: "Offers",  always: false },
  { href: "/despre",  ro: "Despre",  en: "About",   always: true },
  { href: "/reviews", ro: "Recenzii",en: "Reviews", always: true },
];

export default function PublicHeader() {
  const { lang, setLang, currency, setCurrency, t } = useI18n();
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasOffers, setHasOffers] = useState(true);

  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";
  const NAV = BASE_NAV.filter((l) => l.always || hasOffers);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetchOffersCount().then((n) => setHasOffers(n > 0));
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const anchor = href.slice(2);
      if (location.pathname === "/") {
        document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" }), 350);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <>
      {/* ── Fixed header bar ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(5,5,5,0.95)" : "rgba(5,5,5,0.72)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderBottom: `1px solid ${scrolled ? "rgba(212,175,55,0.18)" : "rgba(255,255,255,0.05)"}`,
        }}
        data-testid="public-header"
      >
        {/* Gold pinstripe */}
        <div style={{ height: 1, background: "linear-gradient(90deg, transparent 0%, var(--brand-primary) 20%, var(--brand-primary) 80%, transparent 100%)", opacity: scrolled ? 0.65 : 0.35, transition: "opacity 500ms" }} />

        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 flex items-center justify-between" style={{ height: 62 }}>

          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="brand-link">
            {settings?.logo_path && (
              <img src={resolveUrl(settings.logo_path)} alt={brandName} className="h-7 sm:h-8 w-auto" />
            )}
            {!(settings?.logo_only_mode && settings?.logo_path) && (
              <span
                className="font-serif whitespace-nowrap"
                style={{ fontSize: "clamp(0.95rem, 2.5vw, 1.2rem)", letterSpacing: "0.04em", color: "var(--brand-text)" }}
              >
                {brandName}
              </span>
            )}
          </Link>

          {/* Desktop nav — center */}
          <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2" aria-label="Primary navigation">
            {NAV.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="label-eyebrow transition-colors duration-200 hover:text-[var(--brand-primary)]"
                style={{ color: "var(--brand-text-secondary)" }}
              >
                {lang === "en" ? l.en : l.ro}
              </button>
            ))}
          </nav>

          {/* Right controls */}
          <div className="flex items-center gap-5 shrink-0">

            {/* Lang + Currency */}
            <div className="hidden sm:flex items-center gap-4">
              <button
                onClick={() => setLang(lang === "ro" ? "en" : "ro")}
                className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="lang-switch"
              >
                {lang === "ro" ? "EN" : "RO"}
              </button>
              <span style={{ color: "var(--brand-border)", lineHeight: 1 }}>·</span>
              <button
                onClick={() => setCurrency(currency === "EUR" ? "RON" : "EUR")}
                className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="currency-switch"
              >
                {currency}
              </button>
            </div>

            {/* Auth */}
            {user && user !== false && (
              <div className="hidden sm:flex items-center gap-3">
                <button
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/owner")}
                  className="hover:text-[var(--brand-primary)] transition-colors"
                  style={{ color: "var(--brand-text-secondary)" }}
                  data-testid="nav-dashboard"
                  aria-label="Dashboard"
                >
                  <User className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="hover:text-[var(--brand-primary)] transition-colors"
                  style={{ color: "var(--brand-text-secondary)" }}
                  data-testid="nav-logout"
                  aria-label="Logout"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden flex flex-col justify-center items-end gap-[5px] w-9 h-9"
              aria-label={open ? "Inchide meniu" : "Meniu"}
              aria-expanded={open}
              data-testid="hamburger"
            >
              <span className="header-bar" style={{
                width: 22,
                transform: open ? "translateY(6.5px) rotate(45deg)" : "none",
                background: open ? "var(--brand-primary)" : "var(--brand-text)",
              }} />
              <span className="header-bar" style={{
                width: open ? 14 : 18,
                opacity: open ? 0 : 1,
                background: "var(--brand-text)",
              }} />
              <span className="header-bar" style={{
                width: open ? 22 : 12,
                transform: open ? "translateY(-6.5px) rotate(-45deg)" : "none",
                background: open ? "var(--brand-primary)" : "var(--brand-text)",
              }} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile full-screen overlay ── */}
      <div
        className="fixed inset-0 z-40 lg:hidden flex flex-col"
        style={{
          background: "var(--brand-bg)",
          transform: open ? "none" : "translateY(-8px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 320ms ease, transform 320ms cubic-bezier(0.22,1,0.36,1)",
        }}
        aria-hidden={!open}
      >
        {/* Spacer for header height */}
        <div style={{ height: 63, borderBottom: "1px solid var(--brand-border)" }} />

        {/* Watermark brand */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden>
          <span
            className="font-serif font-light text-center px-4"
            style={{ fontSize: "clamp(3rem, 18vw, 8rem)", color: "rgba(212,175,55,0.04)", lineHeight: 1, letterSpacing: "-0.03em" }}
          >
            {brandName}
          </span>
        </div>

        {/* Nav items */}
        <div className="flex-1 flex flex-col items-center justify-center gap-1 relative z-10 px-6">
          {NAV.map((l, i) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="mobile-nav-item"
              style={{ animationDelay: open ? `${60 + i * 55}ms` : "0ms" }}
            >
              {lang === "en" ? l.en : l.ro}
            </button>
          ))}
        </div>

        {/* Bottom controls */}
        <div
          className="px-8 pb-10 pt-6 flex items-center justify-between"
          style={{ borderTop: "1px solid var(--brand-border)" }}
        >
          <div className="flex items-center gap-5">
            <button
              onClick={() => setLang(lang === "ro" ? "en" : "ro")}
              className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors"
              style={{ color: "var(--brand-text-secondary)" }}
              data-testid="lang-switch-mobile"
            >
              {lang === "ro" ? "EN" : "RO"}
            </button>
            <span style={{ color: "var(--brand-border)" }}>·</span>
            <button
              onClick={() => setCurrency(currency === "EUR" ? "RON" : "EUR")}
              className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors"
              style={{ color: "var(--brand-text-secondary)" }}
              data-testid="currency-switch-mobile"
            >
              {currency}
            </button>
          </div>

          {user && user !== false && (
            <div className="flex items-center gap-5">
              <button
                onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/owner"); }}
                className="label-eyebrow hover:text-[var(--brand-primary)] transition-colors flex items-center gap-1.5"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="nav-dashboard-mobile"
              >
                <User className="w-3.5 h-3.5" />
                {user.role === "admin" ? "Admin" : "Cont"}
              </button>
              <button
                onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                className="hover:text-[var(--brand-primary)] transition-colors"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="nav-logout-mobile"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
